<template>
  <div>
    <header-component />
    <div class="px-4 py-4">

      <!-- Header row -->
      <div class="flex justify-between items-center mb-5">
        <h2 class="text-lg font-bold text-gray-800">Branch Transfers</h2>
        <router-link to="/branch-transfers/create">
          <base-button size="sm" type="primary">+ New Transfer</base-button>
        </router-link>
      </div>

      <!-- Filters -->
      <div class="flex flex-wrap gap-3 mb-4 items-end">
        <div>
          <label class="block text-xs text-gray-500 mb-1">Status</label>
          <select v-model="filters.status" @change="fetchTransfers"
            class="border border-gray-300 rounded px-3 py-2 text-sm">
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-500 mb-1">From Branch</label>
          <select v-model="filters.fromBranchId" @change="fetchTransfers"
            class="border border-gray-300 rounded px-3 py-2 text-sm">
            <option value="">All</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-500 mb-1">To Branch</label>
          <select v-model="filters.toBranchId" @change="fetchTransfers"
            class="border border-gray-300 rounded px-3 py-2 text-sm">
            <option value="">All</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>

      <!-- Table -->
      <div v-else class="overflow-x-auto">
        <table class="min-w-full bg-white border border-gray-200 rounded-lg">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">To</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="transfers.length === 0">
              <td colspan="7" class="text-center py-12 text-gray-400">No transfers found.</td>
            </tr>
            <tr v-for="t in transfers" :key="t.id" class="border-b hover:bg-gray-50">
              <td class="px-4 py-3 text-sm text-gray-800">{{ t.from_branch_name }}</td>
              <td class="px-4 py-3 text-sm text-gray-800">{{ t.to_branch_name }}</td>
              <td class="px-4 py-3 text-sm text-gray-800">₹ {{ Number(t.total_amount).toFixed(2) }}</td>
              <td class="px-4 py-3">
                <span :class="statusClass(t.status)"
                  class="px-2 py-0.5 rounded-full text-xs font-medium capitalize">
                  {{ t.status }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-gray-600">{{ t.created_by_name }}</td>
              <td class="px-4 py-3 text-sm text-gray-600">{{ formatDate(t.created_at) }}</td>
              <td class="px-4 py-3">
                <router-link :to="`/branch-transfers/${t.id}`"
                  class="text-blue-600 text-xs hover:underline">View</router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  </div>
</template>

<script>
import Api from "@/middleware/api";
import endpoints from "@/utils/constants";

const BASE = endpoints.LARAVEL_BASE_URL;

export default {
  name: "BranchTransferList",
  data() {
    return {
      transfers: [],
      branches: [],
      loading: false,
      filters: { status: "", fromBranchId: "", toBranchId: "" },
    };
  },
  created() {
    this.fetchBranches();
    this.fetchTransfers();
  },
  methods: {
    async fetchBranches() {
      try {
        const res = await new Api().call("get", `${BASE}/api/v1/branches`);
        this.branches = res.data.data || res.data;
      } catch (e) {
        console.error(e);
      }
    },
    async fetchTransfers() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.filters.status)       params.set("status",         this.filters.status);
        if (this.filters.fromBranchId) params.set("from_branch_id", this.filters.fromBranchId);
        if (this.filters.toBranchId)   params.set("to_branch_id",   this.filters.toBranchId);
        const res = await new Api().call("get", `${BASE}/api/v1/branch-transfers?${params}`);
        this.transfers = res.data.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    statusClass(status) {
      return {
        pending:  "bg-yellow-100 text-yellow-800",
        accepted: "bg-green-100 text-green-800",
        rejected: "bg-red-100 text-red-800",
      }[status] || "bg-gray-100 text-gray-600";
    },
    formatDate(val) {
      if (!val) return "—";
      return new Date(val + "Z").toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },
  },
};
</script>
